/**
 * Portal Init Domain Types
 */

// ============================================================================
// Shared Types
// ============================================================================

export interface UserProfile {
  id: number
  documentId: string
  username: string
  email: string
  fullName?: string | null
  phone?: string | null
  companyName?: string | null
  tenant: TenantSummary | null
  role: RoleSummary
  /** documentId of the factory assigned to factory-scoped roles */
  factoryDocumentId?: string | null
  /** documentId of the direct user→location relation (fallback when no factory) */
  locationDocumentId?: string | null
}

export interface TenantSummary {
  id: number
  documentId: string
  code: string
  name: string
  tenantStatus: string
  channel: 'b2b' | 'ecommerce'
}

export interface RoleSummary {
  id: number
  documentId?: string
  name: string
  type: string
}

export interface FeatureFlags {
  enableBetaFeatures: boolean
  enableAnalytics: boolean
  enableCopilot: boolean
}

// ============================================================================
// Guest Types (unauthenticated)
// ============================================================================

export interface PriceListSummary {
  id: number
  documentId: string
  code: string
  name: string
  currency: string
}

export interface GuestInitData {
  type: 'guest'
  featureFlags: FeatureFlags
  ecommerce: {
    priceList: PriceListSummary | null
  }
}

// ============================================================================
// Ecommerce Types
// ============================================================================

export interface CustomerGroupSummary {
  id: number
  documentId: string
  code: string
  name: string
}

export interface PricingProfile {
  user: {
    id: number
    email: string
    username: string
  }
  tenant: {
    id: number
    documentId: string
    name: string
    channel: 'b2b' | 'ecommerce'
  } | null
  customerGroup: CustomerGroupSummary | null
  priceList: PriceListSummary | null
}

export interface EcommerceInitData {
  type: 'ecommerce'
  profile: UserProfile
  pricingProfile: PricingProfile | null
  featureFlags: FeatureFlags
}

// ============================================================================
// Customer Types
// ============================================================================

export interface CustomerAccountSummary {
  id: number
  documentId: string
  accountNumber: string
  creditLimit: number
  paymentTerms: string
  accountStatus: string
  vibanNumber?: string | null
  allowedRegions?: RegionSummary[]
  allowedCities?: CitySummary[]
}

export interface RegionSummary {
  id: number
  documentId: string
  code: string
  name: string
}

export interface CitySummary {
  id: number
  documentId: string
  code: string
  name: string
}

export interface UserSettings {
  language: string
  timezone: string
  notifications: {
    email: boolean
    push: boolean
    sms: boolean
  }
  preferences: Record<string, unknown>
}

export interface CustomerInitData {
  type: 'customer'
  profile: UserProfile
  customerAccount: CustomerAccountSummary | null
  settings: UserSettings
  featureFlags: FeatureFlags
}

// ============================================================================
// Admin Types
// ============================================================================

export interface Permission {
  action: string
  subject: string
  conditions?: Record<string, unknown>
}

export interface PermissionsMatrix {
  canManageTenants: boolean
  canManageUsers: boolean
  canManageCustomerAccounts: boolean
  canViewAllOrders: boolean
  canApproveOrders: boolean
  canManageFleet: boolean
  canViewReports: boolean
  detailed: Permission[]
}

export interface AdminInitData {
  type: 'admin'
  profile: UserProfile
  permissions: PermissionsMatrix
  featureFlags: FeatureFlags
}

// ============================================================================
// Unified Types
// ============================================================================

export type InitData = GuestInitData | EcommerceInitData | CustomerInitData | AdminInitData

export type InitType = 'guest' | 'ecommerce' | 'customer' | 'admin'

export type InitStatus = 'idle' | 'loading' | 'success' | 'error'

export interface PortalInitState {
  status: InitStatus
  data: InitData | null
  error: string | null
  lastInitialized: string | null
}

// ============================================================================
// Type Guards
// ============================================================================

export function isGuestInit(data: InitData | null): data is GuestInitData {
  return data?.type === 'guest'
}

export function isEcommerceInit(data: InitData | null): data is EcommerceInitData {
  return data?.type === 'ecommerce'
}

export function isCustomerInit(data: InitData | null): data is CustomerInitData {
  return data?.type === 'customer'
}

export function isAdminInit(data: InitData | null): data is AdminInitData {
  return data?.type === 'admin'
}

export function isAuthenticatedInit(
  data: InitData | null
): data is EcommerceInitData | CustomerInitData | AdminInitData {
  return data !== null && data.type !== 'guest'
}

// ============================================================================
// Constants
// ============================================================================

export const ADMIN_ROLE_TYPES = ['super_admin', 'admin', 'operator'] as const
export const CUSTOMER_ROLE_TYPES = ['customer', 'ecommerce', 'authenticated'] as const
